import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import brain from "@/assets/quantum-mind-brain.png.asset.json";

const EASE = [0.16, 1, 0.3, 1] as const;

function Particles() {
  const dots = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        id: i,
        left: `${(i * 37) % 100}%`,
        top: `${(i * 61) % 100}%`,
        size: 1.5 + ((i * 7) % 3),
        delay: (i % 11) * 0.35,
        duration: 6 + (i % 5),
      })),
    [],
  );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d) => (
        <motion.span
          key={d.id}
          className="absolute rounded-full bg-white/50"
          style={{
            left: d.left,
            top: d.top,
            width: d.size,
            height: d.size,
            willChange: "transform, opacity",
          }}
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: [0, 0.7, 0], y: [-6, -34, -60] }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/** Premium cinematic intro. Shown once per browser session. */
export function SplashScreen({ onDone }: { onDone: () => void }) {
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(true);
  const doneRef = useRef(false);

  useEffect(() => {
    const total = reduced ? 900 : 3500;
    const t = window.setTimeout(() => setVisible(false), total);
    return () => window.clearTimeout(t);
  }, [reduced]);

  function finish() {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }

  const word = "The";

  return (
    <AnimatePresence onExitComplete={finish}>
      {visible ? (
        <motion.div
          key="splash"
          className="fixed inset-0 z-[999] flex items-center justify-center overflow-hidden"
          style={{
            background:
              "radial-gradient(ellipse 90% 60% at 50% 30%, #17224a 0%, #0d1430 45%, #070a18 100%)",
          }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          {/* ambient moving glow */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 size-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[110px]"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(56,189,248,0.28), rgba(168,85,247,0.26), rgba(34,211,238,0.22), rgba(56,189,248,0.28))",
              willChange: "transform, opacity",
            }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={reduced ? { opacity: 0.5 } : { opacity: [0.25, 0.55, 0.4], scale: [0.85, 1.05, 1] }}
            transition={{ duration: 3.2, ease: "easeInOut" }}
          />
          {!reduced ? <Particles /> : null}

          <div className="relative flex flex-col items-center px-8">
            <motion.img
              src={brain.url}
              alt="The Quantum MIND"
              className="size-28 object-contain drop-shadow-[0_0_38px_rgba(120,90,255,0.55)]"
              style={{ willChange: "transform, opacity" }}
              initial={{ opacity: 0, scale: 0.92, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: reduced ? 0.4 : 1, ease: EASE }}
            />

            {/* glowing sweep line */}
            {!reduced ? (
              <motion.div
                aria-hidden
                className="pointer-events-none absolute top-1/2 h-px w-[320px] max-w-[80vw]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.95) 55%, transparent)",
                  boxShadow: "0 0 18px rgba(190,220,255,0.85)",
                  willChange: "transform, opacity",
                }}
                initial={{ x: "-120%", opacity: 0 }}
                animate={{ x: ["-120%", "0%", "120%"], opacity: [0, 1, 0] }}
                transition={{ duration: 1.9, delay: 0.35, ease: EASE, times: [0, 0.55, 1] }}
              />
            ) : null}

            <div className="mt-7 flex flex-col items-center">
              <div className="relative flex items-baseline gap-[0.3em] text-[clamp(1.9rem,8vw,2.9rem)] leading-none text-white">
                {/* "The" — letter by letter */}
                <span className="font-brand-serif inline-flex italic">
                  {word.split("").map((ch, i) => (
                    <motion.span
                      key={i}
                      style={{ willChange: "transform, opacity" }}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: reduced ? 0.3 : 0.5,
                        delay: reduced ? 0 : 0.55 + i * 0.09,
                        ease: EASE,
                      }}
                    >
                      {ch}
                    </motion.span>
                  ))}
                </span>

                {/* "Quantum" — mask reveal */}
                <motion.span
                  className="font-brand-serif inline-block overflow-hidden italic"
                  style={{ willChange: "clip-path" }}
                  initial={{ clipPath: "inset(0 100% 0 0)", opacity: 0 }}
                  animate={{ clipPath: "inset(0 0% 0 0)", opacity: 1 }}
                  transition={{
                    duration: reduced ? 0.3 : 0.95,
                    delay: reduced ? 0 : 1.15,
                    ease: EASE,
                  }}
                >
                  Quantum
                </motion.span>
              </div>

              {/* MIND */}
              <motion.div
                className="mt-2 text-[clamp(2.1rem,9vw,3.2rem)] font-extrabold leading-none tracking-[0.08em] text-white"
                style={{ willChange: "transform, opacity" }}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  textShadow: "0 0 30px rgba(150,180,255,0.55)",
                }}
                transition={{
                  duration: reduced ? 0.35 : 0.7,
                  delay: reduced ? 0.05 : 2.35,
                  ease: EASE,
                }}
              >
                MIND
              </motion.div>

              {/* golden sweep across the lockup */}
              {!reduced ? (
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-[-10%] top-0 bottom-0"
                  style={{
                    background:
                      "linear-gradient(105deg, transparent 40%, rgba(255,226,168,0.5) 50%, transparent 60%)",
                    mixBlendMode: "screen",
                    willChange: "transform, opacity",
                  }}
                  initial={{ x: "-60%", opacity: 0 }}
                  animate={{ x: ["-60%", "60%"], opacity: [0, 1, 0] }}
                  transition={{ duration: 1.1, delay: 2.95, ease: "easeInOut" }}
                />
              ) : null}
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
